// The view-as LENS — the owner's "show me what a manager sees" control.
//
// The server has supported this from the start: `src/worker.js` reads
// `?as=<code>` off the URL and hands it to `viewerContext` (src/access.js),
// which — for an OWNER only — pins `locationCode`, drops `admin`, and sets
// `viewingAs`. A DIRECTOR's or a MANAGER's `?as=` is inert there, deliberately.
// Nothing on the client ever sent the parameter, so the masthead pulldown was
// a disabled placeholder that had never done anything.
//
// This module is the ONE source of truth for the lens. Two rules make it work:
//
//   1. The parameter is appended CENTRALLY (`withViewAs`, used by authedGet,
//      authedWrite and fetchMe), never at a call site. Thirty call sites is
//      thirty chances to forget one, and a forgotten one shows owner-scoped
//      data inside a manager lens — which looks like it worked.
//   2. Changing the lens INVALIDATES everything already fetched. The store
//      caches page reads by key and the snapshot fills entity tables once at
//      sign-in (INV-1: navigation costs zero network calls). Without the
//      invalidation the pages would keep rendering the owner's four clinics
//      under a one-clinic lens — a worse bug than the dead control was.
//
// Persistence is sessionStorage, not localStorage: the lens must survive a
// navigation and a reload (that was the owner's actual complaint), but a NEW
// browser session must start as the owner. A lens that outlives the browser is
// how someone comes in on Monday convinced three clinics have disappeared.
import { signal } from '@preact/signals';
import { clearResourceCache } from './store';

const KEY = 'nvmanager.viewAs';

function readStored(): string | null {
  try {
    const raw = window.sessionStorage.getItem(KEY);
    return raw && raw.trim() ? raw.trim() : null;
  } catch {
    return null;
  }
}

/** The location code being viewed as, or `null` for the owner's own full view. */
export const viewAsLocation = signal<string | null>(readStored());

function persist(code: string | null): void {
  try {
    if (code) window.sessionStorage.setItem(KEY, code);
    else window.sessionStorage.removeItem(KEY);
  } catch {
    // Storage unavailable (private mode, quota). The in-memory signal still
    // holds the lens for this page; only the reload-survives part is lost.
  }
}

/**
 * Append `?as=<code>` to an API path when the lens is on.
 *
 * Preserves any query string the caller already built — several paths arrive
 * here as `/api/v1/tasks?period=...`, and clobbering that with a `?` would
 * silently widen the request instead of narrowing it.
 */
export function withViewAs(path: string): string {
  const code = viewAsLocation.value;
  if (!code) return path;
  const sep = path.includes('?') ? '&' : '?';
  return `${path}${sep}as=${encodeURIComponent(code)}`;
}

/**
 * Switch the lens, then make every screen tell the truth about it.
 *
 * The order matters: the signal is set first so that everything refetched
 * below carries the new `?as=`, then `/api/me` is re-read (it is what drives
 * the clinic strip and the "which clinics are mine" lists), then the cached
 * page reads are dropped, then the snapshot is reloaded — `putEntityTable`
 * REPLACES a table, so rows from the wider view are removed rather than left
 * lying underneath.
 *
 * `session` and `bootstrap` are imported dynamically because both of them
 * reach this module through the API client; a static import here would close
 * the cycle. Same idiom apiClient.ts already uses for `expireSession`.
 */
export async function setViewAs(code: string | null): Promise<void> {
  const next = code && code.trim() ? code.trim() : null;
  if (next === viewAsLocation.value) return;

  viewAsLocation.value = next;
  persist(next);

  const { reloadMe } = await import('./session');
  await reloadMe();

  clearResourceCache();

  const { loadSnapshot } = await import('./bootstrap');
  await loadSnapshot(new Date(), { force: true });
}

/**
 * Drop the lens WITHOUT refetching anything. For sign-out only: the next
 * person at a shared clinic desktop must not inherit the last person's lens,
 * and there is nothing left to refetch by then anyway.
 */
export function resetViewAs(): void {
  viewAsLocation.value = null;
  persist(null);
}
